import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import type { GetShipmentsParams } from '@uturn/api/v2';
import { useShipmentsSearchStore } from '@uturn/portal/store/shipments/shipments-search';
import {
	Accordion,
	AccordionItem,
	Card,
	CardContent,
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
	Icon,
} from '@uturn/ui-kit';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterContext } from './context';
import { DateFilter } from './date-filter';
import { GroupFilter } from './group-filter';
import { RequirementsFilter } from './requirements-filter';
import { ShipmentNumberSorting } from './shipmentnr-sorting';
import { StatusFilter } from './status-filter';
import { TransportTypeFilter } from './transport-type-filter';
import { TsoStatusFilter } from './tso-status-filter';
import { ContainerTypeFilter } from './type-filter';
import { UserFilter } from './user-filter';

function Filters({
	enableTsoFilter,
	enableGroupFilter,
}: {
	/** @deprecated */
	enableTsoFilter: boolean;
	enableGroupFilter: boolean;
}) {
	const { statuses } = useShipmentsSearchStore();

	return (
		<Accordion
			type="multiple"
			defaultValue={['sort', 'status', 'date', 'users', 'groups']}
		>
			<AccordionItem value="sort">
				<ShipmentNumberSorting />
			</AccordionItem>
			{statuses?.length > 1 && (
				<AccordionItem value="status">
					<StatusFilter />
				</AccordionItem>
			)}
			{enableTsoFilter && (
				<AccordionItem value="tso-status">
					<TsoStatusFilter />
				</AccordionItem>
			)}
			<AccordionItem value="date">
				<DateFilter />
			</AccordionItem>
			{enableGroupFilter && (
				<AccordionItem value="groups">
					<GroupFilter />
				</AccordionItem>
			)}
			<AccordionItem value="users">
				<UserFilter />
			</AccordionItem>
			<AccordionItem value="transport">
				<TransportTypeFilter />
			</AccordionItem>
			<AccordionItem value="requirements">
				<RequirementsFilter />
			</AccordionItem>
			<AccordionItem value="size">
				<ContainerTypeFilter />
			</AccordionItem>
		</Accordion>
	);
}

function DesktopFilters({
	enableTsoFilter,
	enableGroupFilter,
}: {
	/** @deprecated */
	enableTsoFilter: boolean;
	enableGroupFilter: boolean;
}) {
	return (
		<div className="hidden lg:block">
			<Filters
				enableGroupFilter={enableGroupFilter}
				enableTsoFilter={enableTsoFilter}
			/>
		</div>
	);
}

function MobileFilters({
	enableTsoFilter,
	enableGroupFilter,
}: {
	/** @deprecated */
	enableTsoFilter: boolean;
	enableGroupFilter: boolean;
}) {
	const [open, setOpen] = useState(false);
	const { t } = useTranslation();

	return (
		<Collapsible open={open} onOpenChange={setOpen} className="block lg:hidden">
			<CollapsibleTrigger className="w-full">
				<Card className="flex h-9 cursor-pointer items-center justify-between px-4 hover:bg-secondary dark:hover:bg-muted/20">
					<p className="text-sm font-medium">
						{t('pages.shipments.filters.title', 'Filters')}
					</p>
					<Icon icon={open ? faChevronUp : faChevronDown} />
				</Card>
			</CollapsibleTrigger>
			<CollapsibleContent>
				<Card className="mx-4 mt-2">
					<CardContent>
						<Filters
							enableGroupFilter={enableGroupFilter}
							enableTsoFilter={enableTsoFilter}
						/>
					</CardContent>
				</Card>
			</CollapsibleContent>
		</Collapsible>
	);
}

export function ShipmentFilter({
	onChange,
	initialFilters,
	enableTsoFilter,
	enableGroupFilter,
}: {
	onChange(filters: Partial<GetShipmentsParams> | null): void;
	initialFilters: Partial<GetShipmentsParams>;
	/** @deprecated */
	enableTsoFilter: boolean;
	enableGroupFilter: boolean;
}) {
	const [filters, setFiltersState] =
		useState<Partial<GetShipmentsParams>>(initialFilters);

	// This is a helper function that allows us to update the filters
	// without having to pass the entire filters object to the setFilters function.
	const setFilters = (filterChanges: Partial<GetShipmentsParams>) => {
		const newFilters = { ...filters, ...filterChanges };
		setFiltersState(newFilters);
		onChange(newFilters);
	};

	return (
		<FilterContext.Provider
			value={{
				filters,
				setFilters,
			}}
		>
			<>
				<DesktopFilters
					enableGroupFilter={enableGroupFilter}
					enableTsoFilter={enableTsoFilter}
				/>
				<MobileFilters
					enableGroupFilter={enableGroupFilter}
					enableTsoFilter={enableTsoFilter}
				/>
			</>
		</FilterContext.Provider>
	);
}
